import type { RecipeDef, StageDef } from "@reagent/sim";
import { freshCardInstances } from "@reagent/sim";
import { ALL_CARDS } from "../../cards";
import { pollutionCardsFor, reachablePollutionReasons } from "../../pollution";
import { setupOfferFor, validateStage } from "../../schema";
import type { StageSolution } from "../../solutions";
import { ERA_1_SCALARS } from "./shared";

/**
 * Era 1's Tyrian Purple pathway (spec 0037, `docs/design/06-eras-and-stages.md`
 * §4.3/§4.6): a core line that crushes murex into gland and lime, macerates,
 * heats twice and cures in two gated holds, and a refined line that dips the
 * purple in alkali. Every number below marked PLACEHOLDER is a stage-author
 * guess chosen so the worked solution clears; none has been playtested.
 *
 * Both lines pre-place their reactor. A separator's `main` edge has to be
 * authored (a bought pipe carries no `separationRole`, so the crusher would hold
 * its gland forever), which needs a real node at the far end; and pre-placing
 * both reactors lets each bind to its own recipe with `recipeIds` (spec 0031),
 * so the core's maceration and the refined dip never see each other's inputs
 * once the two lines share a plant.
 *
 * Budget feeds carry `Sludge`, and a packet with a species no recipe names is
 * not "awaiting a partner", so a Budget feed that lands before its reaction
 * partner is pushed on unreacted, while one landing a tick after leaves surplus
 * partner in the pool to ride along as impurity. Each reactor is laid out so
 * its feeds arrive on the same tick (path lengths differ by the crusher's one
 * tick of processing in the core).
 */

// PLACEHOLDER (spec 0037): needs playtest. Recipes are the shipped Tyrian Purple
// chain (spec 0025) under pathway-prefixed ids, plus the refined dip.

/**
 * Separator: the gland goes down `main`, the lime down `waste`. The recipe names
 * both species so that Lime appears in the core's recipe outputs, which is what
 * makes it a declarable export (validatePool); the tank emits them 1:1 so the
 * multiple never strands gland in the waste fraction.
 */
export const TP_CRUSH: RecipeDef = {
  id: "tp-crush",
  inputs: { MurexGland: 1, Lime: 1 },
  outputs: { MurexGland: 1, Lime: 1 },
  isExothermic: false,
  heat: 0,
  nodeKinds: ["separator"],
  primarySpecies: ["MurexGland"],
};

export const TP_MACERATION: RecipeDef = {
  id: "tp-maceration",
  inputs: { MurexGland: 1, Brine: 1 },
  outputs: { CrudeDye: 1 },
  isExothermic: false,
  heat: 0,
  nodeKinds: ["reactor"],
};

/** Gated at 80C, which a product born at ambient only reaches through two heaters in series (spec 0025). */
export const TP_VAT_HOLD: RecipeDef = {
  id: "tp-vat-hold",
  inputs: { CrudeDye: 1 },
  outputs: { LeucoDye: 1 },
  isExothermic: false,
  heat: 0,
  nodeKinds: ["incubator"],
  holdDuration: 2,
  minTemperature: 80,
};

export const TP_CURING_HOLD: RecipeDef = {
  id: "tp-curing-hold",
  inputs: { LeucoDye: 1 },
  outputs: { TyrianPurple: 1 },
  isExothermic: false,
  heat: 0,
  nodeKinds: ["incubator"],
  holdDuration: 2,
};

/** Refined: an alkali dip at a = 2, so the vat must pool two alkali per purple before it fires. */
export const TP_DIP: RecipeDef = {
  id: "tp-dip",
  inputs: { TyrianPurple: 1, Alkali: 2 },
  outputs: { DipVat: 1 },
  isExothermic: false,
  heat: 0,
  nodeKinds: ["reactor"],
};

/** One heater is enough here: a product born at 20C plus 30C meets the 50C gate exactly. */
export const TP_DIP_HOLD: RecipeDef = {
  id: "tp-dip-hold",
  inputs: { DipVat: 1 },
  outputs: { Dibapha: 1 },
  isExothermic: false,
  heat: 0,
  nodeKinds: ["incubator"],
  holdDuration: 2,
  minTemperature: 50,
};

export const TP_RECIPES: readonly RecipeDef[] = [
  TP_CRUSH,
  TP_MACERATION,
  TP_VAT_HOLD,
  TP_CURING_HOLD,
  TP_DIP,
  TP_DIP_HOLD,
];

/** Maps every reachable pollution reason (spec 0029) rather than hand-listing them, so a change to the board cannot leave one unmapped. */
function finish(bare: StageDef): StageDef {
  return validateStage(
    { ...bare, pollutionCards: pollutionCardsFor(...reachablePollutionReasons(bare, ALL_CARDS)) },
    ALL_CARDS,
  );
}

const card = (id: string) => {
  const found = ALL_CARDS.find((c) => c.id === id);
  if (!found) throw new Error(`tp: unknown card ${id}`);
  return found;
};

// PLACEHOLDER (spec 0037): needs playtest. Feed sizes are chosen so a Budget
// feed's Sludge (up to 2 units once the market doubles it) still leaves a
// hopper packet above ERA_1_SCALARS.minPurity: six units of product against two of Sludge.
const CORE_COMMODITY_PER_TICK = 6;
// PLACEHOLDER (spec 0037): needs playtest. Budget undercuts Premium by 5 credits; the market may double Budget.
const BRINE_BUDGET_COST = 5;
const BRINE_PREMIUM_COST = 10;
// PLACEHOLDER (spec 0037): needs playtest. Cured product wanted at the hopper: four ticks of full flow.
const CORE_QUOTA = 24;
// PLACEHOLDER (spec 0037): needs playtest. Rig 80 + Budget Brine at 2x (10) fits with 10 spare; the Mousetrap (15) does not fit beside the rig.
const CORE_SETUP_BUDGET = 100;

/**
 * Tyrian Purple core. Lesson: Heat + Dwell. Only the crusher, the reactor, the
 * midden and the hopper are pre-placed; the player buys two heaters and two
 * incubators and pipes the line together in Phase 1. The unlidded Lime midden is
 * the stench hook (`03-hazards.md` §3.3): it accrues `verminRisk` every turn.
 */
export const TP_CORE: StageDef = finish({
    id: "era1-tp-core",
    name: "Tyrian Purple: the vat",
    blurb: "Crush the shells, cook the glands, and cure the dye before it turns to sludge.",
    cols: 14,
    rows: 5,
    nodes: [
      { id: "tp-murex", kind: "inputTank", pos: { x: 0, y: 1 }, emits: { MurexGland: CORE_COMMODITY_PER_TICK, Lime: CORE_COMMODITY_PER_TICK } },
      {
        id: "tp-brine",
        kind: "inputTank",
        pos: { x: 0, y: 3 },
        emits: { Brine: CORE_COMMODITY_PER_TICK },
        sourcing: {
          budget: { impurities: { Sludge: 1 }, costCredits: BRINE_BUDGET_COST },
          premium: { costCredits: BRINE_PREMIUM_COST },
        },
      },
      { id: "tp-crusher", kind: "separator", pos: { x: 2, y: 1 }, recipeIds: ["tp-crush"] },
      { id: "tp-reactor", kind: "reactor", pos: { x: 4, y: 1 }, recipeIds: ["tp-maceration"] },
      { id: "tp-midden", kind: "wasteBuyer", pos: { x: 13, y: 0 } },
      { id: "tp-hopper", kind: "productHopper", pos: { x: 13, y: 1 }, bufferCapacity: 99 },
    ],
    edges: [
      {
        id: "tp-feed",
        fromNodeId: "tp-murex",
        toNodeId: "tp-crusher",
        path: [{ x: 0, y: 1 }, { x: 1, y: 1 }, { x: 2, y: 1 }],
      },
      {
        id: "tp-gland",
        fromNodeId: "tp-crusher",
        toNodeId: "tp-reactor",
        separationRole: "main",
        path: [{ x: 2, y: 1 }, { x: 3, y: 1 }, { x: 4, y: 1 }],
      },
      {
        id: "tp-shell",
        fromNodeId: "tp-crusher",
        toNodeId: "tp-midden",
        separationRole: "waste",
        path: [
          { x: 2, y: 1 },
          { x: 2, y: 0 },
          ...Array.from({ length: 11 }, (_, i) => ({ x: 3 + i, y: 0 })),
        ],
      },
    ],
    recipes: [TP_CRUSH, TP_MACERATION, TP_VAT_HOLD, TP_CURING_HOLD],
    objective: { targetComposition: { TyrianPurple: CORE_QUOTA }, minPurity: ERA_1_SCALARS.minPurity },
    energyBudgetKw: ERA_1_SCALARS.energyBudgetKw,
    handSize: ERA_1_SCALARS.handSize,
    ticksPerTurn: ERA_1_SCALARS.ticksPerTurn,
    maxTurns: ERA_1_SCALARS.maxTurns,
    liveBuild: ERA_1_SCALARS.liveBuild,
    corrosiveSpecies: [],
    startingDeck: freshCardInstances([
      "card-solvent-flush",
      "card-emergency-flush",
      "card-rush-order",
      "card-overtime",
      "card-stir-the-vat",
      "card-lid",
    ]),
    setupBudgetCredits: CORE_SETUP_BUDGET,
    setupCatalogue: [
      setupOfferFor(card("card-tp-maceration-vat")),
      setupOfferFor(card("card-mixer")),
      setupOfferFor(card("card-mixer-heavy")),
      setupOfferFor(card("card-heater")),
      setupOfferFor(card("card-incubator")),
      setupOfferFor(card("card-pipe-glass")),
      setupOfferFor(card("card-pipe-ptfe")),
      // 03-hazards.md §3.3 quotes the install at 15 credits.
      { cardId: "card-facility-mousetrap", costCredits: 15 },
    ],
});

// PLACEHOLDER (spec 0037): needs playtest. Eight units of purple against sixteen of alkali is exactly one
// multiple of the a = 2 ratio per unit, so each arrival fires the vat at k = 8. Sludge is 1 + 1 (2 under
// the market's 2x): 8 of 11 stays above ERA_1_SCALARS.minPurity.
const REFINED_PURPLE_PER_TICK = 8;
const REFINED_ALKALI_PER_TICK = 16;
// PLACEHOLDER (spec 0037): needs playtest. Two full packets of dipped product.
const REFINED_QUOTA = 16;
// PLACEHOLDER (spec 0037): needs playtest. Rig 50 + both Budget contracts (5 + Alkali at 2x = 10) fits with 5 spare.
const REFINED_SETUP_BUDGET = 70;
// PLACEHOLDER (spec 0037): needs playtest. The core-product tank's Budget/Premium prices.
const PURPLE_BUDGET_COST = 5;
const PURPLE_PREMIUM_COST = 10;
const ALKALI_BUDGET_COST = 5;
const ALKALI_PREMIUM_COST = 10;

/**
 * Tyrian Purple refined. Lesson: Ratio (k > 1). The dip recipe wants two alkali
 * per purple, so the reactor must pool a whole multiple before it fires; both
 * feeds arrive together (equal pipe lengths), which a Budget feed requires.
 * Only the heater and the incubator are bought: one heater is exactly the
 * 50C the dip hold is gated at.
 */
export const TP_REFINED: StageDef = finish({
  id: "era1-tp-refined",
  name: "Tyrian Purple: the alkali dip",
  blurb: "Dip cured purple in alkali, hot enough to take and no longer than it can bear.",
  cols: 14,
  rows: 5,
  nodes: [
    {
      id: "tp-purple",
      kind: "inputTank",
      pos: { x: 0, y: 1 },
      emits: { TyrianPurple: REFINED_PURPLE_PER_TICK },
      sourcing: {
        budget: { impurities: { Sludge: 1 }, costCredits: PURPLE_BUDGET_COST },
        premium: { costCredits: PURPLE_PREMIUM_COST },
      },
    },
    {
      id: "tp-alkali",
      kind: "inputTank",
      pos: { x: 0, y: 3 },
      emits: { Alkali: REFINED_ALKALI_PER_TICK },
      sourcing: {
        budget: { impurities: { Sludge: 1 }, costCredits: ALKALI_BUDGET_COST },
        premium: { costCredits: ALKALI_PREMIUM_COST },
      },
    },
    { id: "tp-dip-reactor", kind: "reactor", pos: { x: 4, y: 2 }, recipeIds: ["tp-dip"] },
    { id: "tp-dip-hopper", kind: "productHopper", pos: { x: 13, y: 2 }, bufferCapacity: 99 },
  ],
  edges: [],
  recipes: [TP_DIP, TP_DIP_HOLD],
  objective: { targetComposition: { Dibapha: REFINED_QUOTA }, minPurity: ERA_1_SCALARS.minPurity },
  energyBudgetKw: ERA_1_SCALARS.energyBudgetKw,
  handSize: ERA_1_SCALARS.handSize,
  ticksPerTurn: ERA_1_SCALARS.ticksPerTurn,
  maxTurns: ERA_1_SCALARS.maxTurns,
  liveBuild: ERA_1_SCALARS.liveBuild,
  corrosiveSpecies: [],
  startingDeck: freshCardInstances([
    "card-solvent-flush",
    "card-emergency-flush",
    "card-rush-order",
    "card-overtime",
    "card-stir-the-vat",
  ]),
  setupBudgetCredits: REFINED_SETUP_BUDGET,
  setupCatalogue: [
    setupOfferFor(card("card-tp-dip-vat")),
    setupOfferFor(card("card-mixer")),
    setupOfferFor(card("card-mixer-heavy")),
    setupOfferFor(card("card-heater")),
    setupOfferFor(card("card-incubator")),
    setupOfferFor(card("card-pipe-glass")),
    setupOfferFor(card("card-pipe-ptfe")),
  ],
});

/**
 * The core plant is bought and piped in Phase 1; the only Play-phase move is
 * sealing the Lime midden. Budget Brine is chosen: its Sludge rides through to
 * the hopper, so the feeds are sized to stay above the purity floor.
 */
export const TP_CORE_SOLUTION: StageSolution = {
  stageId: TP_CORE.id,
  sourcing: { "tp-brine": "budget" },
  setupBuild: [
    { cardId: "card-heater", at: { x: 5, y: 1 } }, // mints n1
    { cardId: "card-heater", at: { x: 6, y: 1 } }, // mints n2
    { cardId: "card-incubator", at: { x: 7, y: 1 } }, // mints n3: vat
    { cardId: "card-incubator", at: { x: 8, y: 1 } }, // mints n4: curing
    { cardId: "card-pipe-glass", fromNodeId: "tp-brine", toNodeId: "tp-reactor" },
    { cardId: "card-pipe-glass", fromNodeId: "tp-reactor", toNodeId: "n1" },
    { cardId: "card-pipe-glass", fromNodeId: "n1", toNodeId: "n2" },
    { cardId: "card-pipe-glass", fromNodeId: "n2", toNodeId: "n3" },
    { cardId: "card-pipe-glass", fromNodeId: "n3", toNodeId: "n4" },
    { cardId: "card-pipe-glass", fromNodeId: "n4", toNodeId: "tp-hopper" },
  ],
  turns: [[{ cardId: "card-lid", at: { x: 13, y: 0 } }]],
  notes:
    "Crusher, reactor, midden and hopper are pre-placed; buy two heaters in series (each adds 30C to a product born at " +
    "20C, so 80C only after both) then the vat and curing incubators, and pipe reactor to hopper. Lid the Lime midden on " +
    "turn 1: unlidded it adds verminRisk every turn and infests a machine by turn 3.",
};

export const TP_REFINED_SOLUTION: StageSolution = {
  stageId: TP_REFINED.id,
  sourcing: { "tp-purple": "budget", "tp-alkali": "budget" },
  setupBuild: [
    { cardId: "card-heater", at: { x: 5, y: 2 } }, // mints n1
    { cardId: "card-incubator", at: { x: 6, y: 2 } }, // mints n2
    { cardId: "card-pipe-glass", fromNodeId: "tp-purple", toNodeId: "tp-dip-reactor" },
    { cardId: "card-pipe-glass", fromNodeId: "tp-alkali", toNodeId: "tp-dip-reactor" },
    { cardId: "card-pipe-glass", fromNodeId: "tp-dip-reactor", toNodeId: "n1" },
    { cardId: "card-pipe-glass", fromNodeId: "n1", toNodeId: "n2" },
    { cardId: "card-pipe-glass", fromNodeId: "n2", toNodeId: "tp-dip-hopper" },
  ],
  turns: [],
  notes:
    "Nothing to play. Buy one heater and one incubator and pipe both feeds into the dip reactor; the reactor pools the " +
    "alkali it needs (a = 2) before firing, one heater puts the product at the hold's 50C gate, and nothing dwells " +
    "long enough to sludge.",
};
